import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Link, useHistory } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import { createBezug, getBezuege, getVistPermissions } from '../../../actions';
import { hasManagementUiAccess } from '../../../helpers/permissions';
import ButtonArea from '../ButtonArea';

import BezugForm from './BezugForm';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const BezugCreate = ({
  pathname,
  createBezug,
  getBezuege,
  bezuege,
  intl,
  token,
  permissions = null,
  getVistPermissions,
}) => {
  const [formData, setFormData] = useState({
    name: '',
  });
  const [error, setError] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [isBezugNameDuplicate, setIsBezugNameDuplicate] = useState(false);

  const isSubmitDisabled = formData.name === '';

  const history = useHistory();
  // Management-table CRUD: Admin + F&E per TASK-90 §2.
  const canEdit = hasManagementUiAccess(permissions);
  // flag to make sure permissions are fully loaded before returning
  // an unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Fetch @vist_permissions if the reducer hasn't seen a response yet.
    if (!permissions && permissionsLoading) {
      getVistPermissions()
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch(() => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setIsClient(true);
    getBezuege({ getAll: true });
  }, [getBezuege]);

  // check for duplicates!
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    if (name === 'name') {
      const isDuplicate = bezuege.some((bezug) => bezug.name === value);
      setIsBezugNameDuplicate(isDuplicate);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Remove fields with empty strings from the formData
    const cleanedFormData = Object.entries(formData).reduce(
      (acc, [key, value]) => {
        if (value !== '') {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    createBezug(cleanedFormData)
      .then((result) => {
        const id = result.id;
        if (id) {
          history.push(`/controlpanel/rezeptdatenbank/bezuege`);
        } else {
          setError('Es ist etwas schiefgelaufen, Bezug wurde nicht angelegt.');
        }
      })
      .catch((error) => {
        setError('Es ist etwas schiefgelaufen, Bezug wurde nicht angelegt');
        setErrorDetail(error.name + ': ' + error.message);
      });
  };
  if (!token) {
    return <Error error={{ status: 401 }} />;
  }
  if (!permissionsLoading && !canEdit) {
    return <Error error={{ status: 401 }} />;
  }
  return (
    <div className="view-wrapper rbd add">
      <Helmet title="Bezug Anlegen" />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1>Bezug Anlegen</h1>
              </div>
              <div className="button-container">
                <ButtonArea
                  isAdd={true}
                  handleSubmit={handleSubmit}
                  isSubmitDisabled={isSubmitDisabled}
                  backURL={'/controlpanel/rezeptdatenbank/bezuege'}
                  typeString={'den Bezug'}
                />
              </div>
            </div>
          </Segment>
          <Segment className="rdbAddEditForm">
            {error && (
              <div className="error-message">
                <h2>{error}</h2>
                <p>{errorDetail}</p>
              </div>
            )}
            <div className="rdb add form bezuege">
              <BezugForm
                isBezugNameDuplicate={isBezugNameDuplicate}
                formData={formData}
                handleInputChange={handleInputChange}
              />
            </div>
          </Segment>
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel/bezugdatenbank" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

BezugCreate.propTypes = {
  pathname: PropTypes.string.isRequired,
  bezuege: PropTypes.array.isRequired,
  createBezug: PropTypes.func.isRequired,
  getBezuege: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  token: PropTypes.string,
  permissions: PropTypes.object,
  getVistPermissions: PropTypes.func,
};

export default compose(
  injectIntl,
  connect(
    (state) => ({
      pathname: state.router.location.pathname,
      bezuege: state.bezuege.data.bezuege,
      token: state.userSession.token,
      permissions: state.permissions.permissions,
    }),
    { createBezug, getBezuege, getVistPermissions },
  ),
)(BezugCreate);
